import { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Input, Modal } from "antd";
import RiderScoreTable from "./rider-score";
import { parseRiderList, parseRiderScoreGroups, parseUsersTable } from "utils/parsers";
import { getRiderScoreListByAvgScore } from "store/actions";
import { RiderScoreListItem } from "protobuf/user_stats_service_pb";
import { searchIcon } from "svgs";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function RiderScoreChart() {
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const chartRef = useRef(null);

  interface RiderData {
    riderName: string;
    // Add other properties as needed
  }

  const [modalData, setModalData] = useState<RiderData[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<"Good" | "Average" | "Bad" | null>(null);
  const { riderScoreGroups, riderList } = useSelector<AppState, AppState["users"]>(
    ((state) => state.users)
  );
  const [searchInput, setSearchInput] = useState("");

  const riderData = useMemo(
    () => parseRiderScoreGroups(riderScoreGroups),
    [riderScoreGroups]
  );

  // Calculate the count of riders in each category (Good, Average, Bad)
  const categoryCounts = {
    Good: riderData?.good ?? 2,
    Average: riderData?.average ?? 2,
    Bad: riderData?.bad ?? 2
  };

  // Chart.js data configuration
  const chartData = {
    labels: ['Bad', 'Average', 'Good'],
    
    datasets: [
      {
        data: [categoryCounts.Bad, categoryCounts.Average, categoryCounts.Good],
        backgroundColor: [
          'rgba(245, 116, 0, 0.2)', // Bad - Orange
          'rgba(114, 48, 200, 0.2)', // Average - Purple
          'rgba(10, 189, 105, 0.34)', // Good - Green
        ],
        borderColor: [
          'rgba(245, 116, 0, 1)',
          'rgba(114, 48, 200, 1)',
          'rgba(10, 189, 105, 1)',
        ],
        borderWidth: 1, 
        barThickness: 30,
        maxBarThickness: 60,
      },
    ],
  };

  // Chart.js options configuration
  const chartOptions = {
    indexAxis: 'y' as const, // Horizontal bar chart
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true, // Disable tooltips as in original
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
        border: {
          display: false,
        },
      },
      y: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#777777',
          font: {
            size: 12,
          },
        },
        border: {
          display: false,
        },
      },
    },
    layout: {
      padding: {
        left: 10,
        right: 5,
        top: 0,
        bottom: 10,
      },
    },
    onClick: (event: any, elements: any) => {
      if (elements.length > 0) {
        const elementIndex = elements[0].index;
        const categories = ['Bad', 'Average', 'Good'];
        const category = categories[elementIndex] as "Good" | "Average" | "Bad";
        handleBarClick({ y: category });
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart' as const,
    },
  };

  useEffect(() => {
    console.log("useEffect triggered. isModalVisible:", isModalVisible, "riderList:", riderList);
    if (isModalVisible && riderList) {
      const rlist = parseRiderList({ rider_list: riderList });
      console.log("Parsed Rider List:", rlist);
      setModalData(rlist);
    }
  }, [riderList, isModalVisible]);

  const filteredModalData = useMemo(() => {
    if (!searchInput) return modalData;
    return modalData.filter((rider) =>
      rider.riderName.toLowerCase().includes(searchInput.toLowerCase())
    );
  }, [searchInput, modalData]);

  // Handle click event on the bars
  const handleBarClick = (datapoint: { y: "Good" | "Average" | "Bad" }) => {
    const category = datapoint.y;
    setSelectedCategory(category);
    setIsModalVisible(true);

    if (category === "Good") {
      dispatch(getRiderScoreListByAvgScore({
        organization_id: "",
        start_date: "2025-01-01",
        end_date: "2025-02-13",
        min_score: 7,
        max_score: 10,
      }));
    } else if (category === "Average") {
      dispatch(getRiderScoreListByAvgScore({
        organization_id: "",
        start_date: "2025-01-01",
        end_date: "2025-02-13",
        min_score: 5,
        max_score: 7,
      }));
    } else {
      dispatch(getRiderScoreListByAvgScore({
        organization_id: "",
        start_date: "2024-01-01",
        end_date: "2025-02-13",
        min_score: 0,
        max_score: 4,
      }));
    }
  };

  return (
    <>
      <h3 style={{ margin: 0, marginBottom: "10px" }}>Rider's Score</h3>
       {(riderData.good > 0 || riderData.average > 0 || riderData.bad > 0) ? (
      <div style={{ height: "180px", width: "100%" }}>
        <Bar
          ref={chartRef}
          data={chartData}
          options={chartOptions}
        />
      </div>
       ): (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "150px",
              textAlign: "center",
            }}
          >
            <p>No records available to display.</p>
          </div>
        )}

      <Modal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedCategory(null);
          setSearchInput("");
        }}
        footer={null}
        width={800}
      >
        <Input
          className="--standard"
          placeholder="Search Rider..."
          prefix={searchIcon}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {filteredModalData.length > 0 ? (
          <RiderScoreTable data={filteredModalData} />
        ) : (
          <div style={{ textAlign: "center", marginTop: "20px", fontSize: "16px", color: "#777" }}>
            No data available for the selected filter.
          </div>
        )}
      </Modal>
    </>
  );
}